// @greenlight-spec: 11-observability.md#control-plane-internal-health
// @greenlight-spec: 26-versioning-update-strategy.md#release-validation
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import {
  RELEASE_VALIDATION_SUITES,
  type ReleaseValidationOutcome,
  type ReleaseValidationRunView,
  type ReleaseValidationSuite,
} from '@greenlight/shared';

const GENERIC_REFUSAL = 'The re-run could not be started.';

const SUITE_LABEL: Record<ReleaseValidationSuite, string> = {
  contract: 'Provider contract suites',
  e2e: 'End-to-end suite',
};

const OUTCOME_BADGE: Record<
  ReleaseValidationOutcome,
  { variant: StatusBadgeVariant; label: string }
> = {
  in_flight: { variant: 'deploying', label: 'Running' },
  passed: { variant: 'active', label: 'Passed' },
  failed: { variant: 'failed', label: 'Failed' },
  timed_out: { variant: 'degraded', label: 'Timed out' },
};

/** The server's own words: the in-flight refusal names the suites still running. */
function refusalMessage(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message !== '' ? message : GENERIC_REFUSAL;
}

function failingLine(run: ReleaseValidationRunView): string | null {
  if (run.outcome === 'passed' || run.outcome === 'in_flight') return null;
  const where = [run.failing_case, run.failing_step].filter((part) => part !== null).join(' · ');
  if (where !== '') return `Failed at ${where}`;
  return run.reason === null ? null : run.reason.replace(/_/g, ' ');
}

function RunDetails({
  run,
  id,
}: {
  readonly run: ReleaseValidationRunView;
  readonly id: string;
}): React.ReactElement {
  return (
    <div
      id={id}
      className="mt-2 space-y-3 text-sm"
      data-testid={`release-validation-${run.suite}-details`}
    >
      {run.cases.length > 0 ? (
        <ul className="space-y-1">
          {run.cases.map((entry) => (
            <li key={entry.id} className="flex flex-wrap gap-x-2 break-words">
              <span className="font-mono">{entry.id}</span>
              <span className="text-muted-foreground">{entry.outcome}</span>
              {entry.step !== null ? (
                <span className="text-muted-foreground">· {entry.step}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {run.debug_excerpt !== null ? (
        <div className="space-y-1">
          <pre
            tabIndex={0}
            aria-label={`${SUITE_LABEL[run.suite]} excerpt`}
            className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-gl border border-border bg-muted/40 p-3 font-mono text-xs"
          >
            {run.debug_excerpt.text}
          </pre>
          {run.debug_excerpt.truncated ? (
            <p className="text-xs text-muted-foreground">Trimmed to fit</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SuiteRow({
  suite,
  run,
}: {
  readonly suite: ReleaseValidationSuite;
  readonly run: ReleaseValidationRunView | undefined;
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const detailsId = React.useId();

  if (run === undefined) {
    return (
      <li
        className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0"
        data-testid={`release-validation-${suite}`}
      >
        <span className="text-sm font-medium text-foreground">{SUITE_LABEL[suite]}</span>
        <StatusBadge variant="pending">Not run yet</StatusBadge>
      </li>
    );
  }

  const badge = OUTCOME_BADGE[run.outcome];
  const failing = failingLine(run);
  const hasDetails = run.debug_excerpt !== null || run.cases.length > 0;
  const passedCases = run.cases.filter((entry) => entry.outcome === 'passed').length;
  const when =
    run.finished_at !== null
      ? `Finished ${relativeTime(run.finished_at)}`
      : `Started ${relativeTime(run.started_at)}`;

  return (
    <li className="py-3 first:pt-0" data-testid={`release-validation-${suite}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{SUITE_LABEL[suite]}</span>
        <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {when}
        {run.cases.length > 0 ? ` · ${passedCases} of ${run.cases.length} cases passed` : ''}
      </p>
      {failing !== null ? <p className="mt-1 text-sm text-foreground">{failing}</p> : null}
      {hasDetails ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mt-1 h-11 md:h-8"
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </Button>
          {expanded ? <RunDetails run={run} id={detailsId} /> : null}
        </>
      ) : null}
    </li>
  );
}

/** What the runs looked like when the re-run was accepted, so the next change in them clears the wait. */
function runsSignature(runs: readonly ReleaseValidationRunView[]): string {
  return runs.map((run) => `${run.suite}:${run.outcome}:${run.started_at}`).join('|');
}

/**
 * Release validation for the running build, on its own tab and not inside the
 * updater card: the installed build and its verdict are separate facts, and an
 * installed build that failed a scenario is never shown as a failed install.
 * Renders nothing on an install that is not validating releases.
 *
 * A suite with no row reads "Not run yet" — a missing verdict is never a pass.
 * Excerpts and case names are bounded text from a test runner and render as
 * inert text only.
 */
export function ReleaseValidationSection({
  pollMs,
}: {
  pollMs: number;
}): React.ReactElement | null {
  const { data } = api.health.releaseValidationStatus.useQuery(
    ['health-release-validation'],
    undefined,
    { queryKey: ['health-release-validation'], refetchInterval: pollMs },
  );
  const rerun = api.health.rerunReleaseValidation.useMutation();
  const [confirming, setConfirming] = React.useState(false);
  const [requestedFrom, setRequestedFrom] = React.useState<string | null>(null);
  const [refusal, setRefusal] = React.useState<string | null>(null);

  const status = data?.status === 200 ? data.body : undefined;
  const runs = status?.runs;
  const signature = runs === undefined ? '' : runsSignature(runs);

  React.useEffect(() => {
    if (requestedFrom !== null && signature !== requestedFrom) setRequestedFrom(null);
  }, [requestedFrom, signature]);

  if (status === undefined || !status.enabled) return null;

  const inFlight = status.runs.some((run) => run.outcome === 'in_flight');
  const pending = rerun.isPending;
  const requested = requestedFrom !== null;
  const build = status.release_version;

  const start = (): void => {
    setRefusal(null);
    rerun.mutate(
      { body: {} },
      {
        // ts-rest resolves every status through this channel, so the 409 refusal
        // arrives here and not as an error.
        onSuccess: (result) => {
          setConfirming(false);
          if (result.status >= 300) {
            setRefusal(refusalMessage(result.body));
            return;
          }
          setRequestedFrom(signature);
        },
        onError: () => {
          setConfirming(false);
          setRefusal(GENERIC_REFUSAL);
        },
      },
    );
  };

  return (
    <SectionCard title="Release validation" testId="release-validation-section">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Verdicts for the running build{build !== null ? ' ' : ''}
          {build !== null ? <span className="font-mono">{build}</span> : null}. These are separate
          from the update that installed it.
        </p>

        <ul className="divide-y divide-border">
          {RELEASE_VALIDATION_SUITES.map((suite) => (
            <SuiteRow
              key={suite}
              suite={suite}
              run={status.runs.find((run) => run.suite === suite)}
            />
          ))}
        </ul>

        <div className="space-y-3">
          <Button
            variant="secondary"
            className="h-11 md:h-9"
            disabled={inFlight || pending || requested}
            onClick={() => setConfirming(true)}
          >
            Re-run validation
          </Button>

          {inFlight ? (
            <p className="text-xs text-muted-foreground">
              A validation run is in progress. Re-run comes back when it finishes.
            </p>
          ) : null}

          {requested ? (
            <Alert>
              <AlertDescription>
                Re-run requested. The verdicts reset when the worker picks it up, usually within a
                minute.
              </AlertDescription>
            </Alert>
          ) : null}

          {refusal !== null ? (
            <Alert variant="destructive">
              <AlertDescription>{refusal}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </div>

      <GLDrawer
        variant="confirm"
        open={confirming}
        onOpenChange={setConfirming}
        mobileBreakpoint={768}
        title="Re-run release validation"
        description={`This asks the control plane to run the contract and end-to-end suites against ${build ?? 'the running build'} again, replacing the verdicts above. The action is audited.`}
        preventDismiss={pending}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={pending}>
              Cancel
            </Button>
            <Button variant="default" onClick={start} disabled={pending}>
              Re-run validation
            </Button>
          </>
        }
      >
        {null}
      </GLDrawer>
    </SectionCard>
  );
}
